import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api/client';
import './Products.css';

const products = [
  {
    id: 'flux',
    slug: 'flux',
    name: 'FLUX',
    logo: '/assets/flux-logo.png',
    description: 'Modular processor with flexible grid-based routing. Design your own signal chain with drag-and-drop simplicity. Includes a built-in NAM player that loads Neural Amp Modeler profiles directly — no conversion required.',
    features: [
      'Modular 10x4 grid routing (hardware-ready)',
      '11 studio-quality DSP modules',
      'Built-in NAM player — load .nam profiles directly, no conversion',
      'IR cabinet loader with factory cabs included',
      'Customizable UI with skinning support',
      'Scene & preset management',
    ],
    screenshots: [
      { src: '/assets/screenshots/flux/overview/full-chain.png', label: 'Signal Chain' },
      { src: '/assets/screenshots/flux/overview/default-view.png', label: 'Default View' },
      { src: '/assets/screenshots/flux/overview/grid-routing.png', label: 'Grid Routing' },
    ],
    formats: 'VST3 / AU / Standalone',
    version: '0.1.1',
    comingSoon: false,
  },
];

function Products({ onAuthRequired }) {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(null);
  const [activeScreenshot, setActiveScreenshot] = useState({});
  const [lightbox, setLightbox] = useState(null);

  const handleDownload = async (product) => {
    if (!user) {
      onAuthRequired();
      return;
    }

    setDownloading(product.slug);
    try {
      const response = await api.post(`/downloads/${product.slug}`);
      if (response.downloadUrl) {
        const a = document.createElement('a');
        a.href = response.downloadUrl;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const getActiveIndex = (productId) => {
    return activeScreenshot[productId] || 0;
  };

  return (
    <section id="products" className="products">
      <div className="container">
        <h2 className="products-title">Products</h2>
        <div className="products-grid">
          {products.map((product) => {
            const idx = getActiveIndex(product.id);
            return (
            <div key={product.id} className="product-card">
              <div className="product-info">
                {product.logo ? (
                  <img src={product.logo} alt={product.name} className="product-logo" />
                ) : (
                  <h3 className="product-name">{product.name}</h3>
                )}
                <span className="product-version">v{product.version}</span>

                {product.screenshots && product.screenshots.length > 0 && (
                  <div className="product-gallery">
                    <img
                      src={product.screenshots[idx].src}
                      alt={product.screenshots[idx].label}
                      className="gallery-hero"
                      onClick={() => setLightbox(product.screenshots[idx])}
                    />
                    <div className="gallery-thumbs">
                      {product.screenshots.map((shot, i) => (
                        <button
                          key={i}
                          className={`gallery-thumb ${i === idx ? 'active' : ''}`}
                          onClick={() => setActiveScreenshot({ ...activeScreenshot, [product.id]: i })}
                        >
                          <img src={shot.src} alt={shot.label} />
                          <span className="thumb-label">{shot.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <p className="product-description">{product.description}</p>
                <ul className="product-features">
                  {product.features.map((feature, index) => (
                    <li key={index}>{feature}</li>
                  ))}
                </ul>
                <p className="product-formats">{product.formats}</p>
              </div>
              {product.comingSoon ? (
                <span className="product-coming-soon">Coming Soon</span>
              ) : (
                <button
                  className="product-download"
                  onClick={() => handleDownload(product)}
                  disabled={downloading === product.slug}
                >
                  {downloading === product.slug
                    ? 'Preparing...'
                    : user
                    ? 'Download'
                    : 'Sign in to Download'}
                </button>
              )}
              <a href="#contact" className="product-support-link">Need help? Contact us</a>
            </div>
            );
          })}
        </div>
      </div>

      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightbox(null)}>&times;</button>
            <img src={lightbox.src} alt={lightbox.label} />
            <p className="lightbox-label">{lightbox.label}</p>
          </div>
        </div>
      )}
    </section>
  );
}

export default Products;
